import axios, {HttpStatusCode} from 'axios';
import BackendResponse from './backend-response';
import appConfig from '@/config';

/**
 * Get メソッド 共通処理
 * @param {object} userInfo ユーザ情報
 * @param {string} execPath 実行パス
 * @param {object} params plain object または URLSearchParams object
 * @param {array} fields 取得したい項目
 * @param {object} [custom] カスタム設定
 * @return {Promise<Object>} 実行結果
 */
async function _getRequestCommon(
    userInfo, execPath, params={}, fields=[], custom=null) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    let _params = {...params};
    if (fields && fields.length > 0) {
      _params['fields'] = JSON.stringify(fields);
    }

    /** @type AxiosRequestConfig<any> */
    const _config = {
      params: _params,
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    };

    // カスタム設定が存在する場合はConfigに追加
    if (custom) {
      _config['custom'] = custom;
    }

    result = await axios.get(appConfig.API_URL + execPath, _config);
    return getResponse(result);
  } catch (err) {
    return getResponse(err.response);
  }
}

/**
 * Get メソッド
 * @param {object} userInfo ユーザ情報
 * @param {string} execPath 実行パス
 * @param {object} params plain object または URLSearchParams object
 * @param {array} fields 取得したい項目
 * @return {Promise<object>} 実行結果
 */
export async function getRequest(
    userInfo, execPath, params={}, fields=[] ) {
  return await _getRequestCommon(
      userInfo, execPath, params, fields,
  );
}

/**
 * Get メソッド(通信中画像表示なし)
 * @param {object} userInfo ユーザ情報
 * @param {string} execPath 実行パス
 * @param {object} params plain object または URLSearchParams object
 * @param {array} fields 取得したい項目
 * @return {Object} 実行結果
 */
export async function getRequestNoImage(
    userInfo, execPath, params={}, fields=[] ) {
  return await _getRequestCommon(
      userInfo, execPath, params, fields,
      {useLoadingImage: false},
  );
}

/**
* Post メソッド 共通処理
* @param {object} userInfo ユーザ情報
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @param {object} [custom] カスタム設定
* @return {Object} 実行結果
*/
async function _postRequestCommon(
    userInfo, execPath, body, fields=[], custom=null) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    let _body = {...body};
    if (fields && fields.length > 0) {
      _body['fields'] = fields;
    }

    /** @type AxiosRequestConfig<any> */
    const _config = {
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    };

    // カスタム設定が存在する場合はConfigに追加
    if (custom) {
      _config['custom'] = custom;
    }

    result = await axios.post(appConfig.API_URL + execPath, _body, _config);
    return getResponse(result);
  } catch (err) {
    return getResponse(err.response);
  }
}

/**
 * download(Post)メソッド
 * @param {string} execPath 実行パス
 * @param {Object} body 入力パラメータ
 * @param {array} fields 取得したい項目
 * @param {object} [customForApi] カスタム設定(API実行時用)
 * @param {object} [customForBlob] カスタム設定(Blob取得時(GET)用)
 * @return {Object} 実行結果
 */
export async function downloadRequest(
    execPath, body, fields=[], customForApi=null, customForBlob=null) {
  // ダウンロードURL取得APIを実行
  const response =
    await _postRequestCommon(null, execPath, body, fields, customForApi);

  // レスポンスヘッダのlocationをダウンロードURLとして取得
  const url = response.headers.location;
  if (url === undefined) {
    return response;
  }

  // Blob StorageからBlob形式でファイル取得
  let response2 = await downloadBlobFromStorage(url, customForBlob);
  // ダウンロードURL取得APIのレスポンス異常（メッセージあり）の場合、保持する
  if (response.status !== HttpStatusCode.Ok && response?.data.message) {
    response2.apiErrorMessage = response.data.message;
  }
  return response2;
}

/**
 * Blob StorageからのBlobダウンロード。
 *
 * @param {string} storageUrl Blobダウンロード用URL
 * @param {object} [custom] カスタム設定
 * @return {Promise<AxiosResponse<any, any>>} Blob取得レスポンス
 */
export async function downloadBlobFromStorage(storageUrl, custom=null) {
  /** @type AxiosRequestConfig<any> */
  const _config = {
    validateStatus: function(status) {
      return validHttpStatus(status);
    },
    responseType: 'blob',
  };

  // カスタム設定が存在する場合はConfigに追加
  if (custom) {
    _config['custom'] = custom;
  }

  // ヘッダの更新/設定をしないカスタムフラグを設定
  Object.assign(_config, {
    custom: {useAuthHeader: false},
  });

  return await axios.get(storageUrl, _config);
}

/**
* Post メソッド
* @param {object} userInfo ユーザ情報
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Object} 実行結果
*/
export async function postRequest(
    userInfo, execPath, body, fields=[]) {
  return await _postRequestCommon(
      userInfo, execPath, body, fields,
  );
}

/**
* Post メソッド(通信中画像表示なし)
* @param {object} userInfo ユーザ情報
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Object} 実行結果
*/
export async function postRequestNoImage(
    userInfo, execPath, body, fields=[]) {
  return await _postRequestCommon(
      userInfo, execPath, body, fields,
      {useLoadingImage: false},
  );
}

/**
* Put メソッド
* @param {object} userInfo ユーザ情報
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @param {array} fields 取得したい項目
* @return {Promise<Object>} 実行結果
*/
export async function putRequest(
    userInfo, execPath, body, fields=[]) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    let _body = {...body};
    if (fields && fields.length > 0) {
      _body['fields'] = fields;
    }
    result = await axios.put(appConfig.API_URL + execPath, _body, {
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    });
    return getResponse(result);
  } catch (err) {
    return getResponse(err.response);
  }
}

/**
* Delete メソッド
* @param {object} userInfo ユーザ情報
* @param {string} execPath 実行パス
* @param {Object} body 入力パラメータ
* @return {Promise<Object>} 実行結果
*/
export async function deleteRequest(
    userInfo, execPath, body) {
  let result;
  if (!execPath) {
    console.log('引数が未設定のため、終了');
    return getResponse(result);
  }

  try {
    result = await axios.delete(appConfig.API_URL + execPath, {
      data: body,
      validateStatus: function(status) {
        return validHttpStatus(status);
      },
    });
  } catch (err) {
    return getResponse(err.response);
  }
  return getResponse(result);
}

export const isInvalidResponse = (response) => {
  return !response?.hasOwnProperty('data');
};

export const getResponse = (response) => {
  return {
    headers: response?.hasOwnProperty('headers') ? response.headers : {},
    status: response?.hasOwnProperty('status') ? response.status : '',
    statusText: response?.hasOwnProperty('statusText') ? response.statusText : '',
    data: getResponseData(response),
  };
};

export const getResponseStatus = (response) => {
  const result = {
    status: response?.hasOwnProperty('status') ? response.status : '',
    statusText: response?.hasOwnProperty('statusText') ? response.statusText : '',
  };
  return result;
};

export const getResponseData = (response) => {
  return response?.hasOwnProperty('data') ? response.data : null;
};

export const getResponseHeaders = (response) => {
  return response?.hasOwnProperty('headers') ? response.headers : null;
};

export const getResponseMethod = (response) => {
  return response.config.method;
};

export const getPath= (url) => {
  if (!url) {
    return '';
  }
  return url.split('api')[url.split('api').length - 1];
};

/**
 * 戻り値に画面項目追加
 * @param {array|object} result 戻り値
 * @param {array} fields 画面項目
 */
export function addFields(result, fields) {
  if (!fields || fields.length == 0) {
    return;
  }
  function setField(obj, field) {
    if (!obj?.hasOwnProperty(field)) {
      obj[field] = '';
    }
  }
  if (Array.isArray(result)) {
    const size = Object.keys(result).length;
    for (let i = 0; i < size; i++) {
      for (const field of fields) {
        setField(result[i], field);
      }
    }
  } else {
    for (const field of fields) {
      setField(result, field);
    }
  }
}

function validHttpStatus(status) {
  return (status >= axios.HttpStatusCode.Ok &&
    status < axios.HttpStatusCode.MultipleChoices) ||
    status == axios.HttpStatusCode.BadRequest;
}

/**
 * バックエンド応答異常.
 */
export class BackendResponseError extends Error {
  /**
   * コンストラクタ
   * @param {string} msg 例外メッセージ
   * @param {object} res レスポンス
   */
  constructor(msg, res) {
    super(msg);
    this.name = 'BackendResponseError';
    this.response = res;
  }
};

/**
 * バックエンド非同期API
 */
export class AsyncBackendRequest {
  /**
   * コンストラクタ
   *
   * @param {function} doStartRequest リクエスト前処理
   * @param {function} doEndRequest リクエスト後処理
   */
  constructor(doStartRequest, doEndRequest) {
    this.doStartRequest = doStartRequest || function() {};
    this.doEndRequest = doEndRequest || function() {};
  }

  /**
   * 非同期API実行
   *
   * - バックエンド非同期処理の実行要求を送信
   * - 実行状態をポーリングして処理終了を待つ
   *
   * @param {string} reqPath リクエストパス
   * @param {object} reqBody リクエストボディ
   * @param {object} options リクエスト設定
   * @return {object} レスポンス(APIの実行結果)
   */
  async post(reqPath, reqBody, options) {
    this.doStartRequest();
    try {
      return await postAsyncRequest(reqPath, reqBody, options);
    } catch (err) {
      return getResponse(err.response);
    } finally {
      this.doEndRequest();
    }
  };
}

/**
 * 非同期API実行
 *
 * - バックエンド非同期処理の実行要求を送信
 * - 実行状態をポーリングして処理終了を待つ
 *
 * @param {string} reqPath リクエストパス
 * @param {object} reqBody リクエストボディ
 * @param {object} options リクエスト設定
 * @return {Promise<object>} レスポンス(APIの実行結果)
 */
async function postAsyncRequest(reqPath, reqBody, options) {
  const timeout = options?.timeout || 60;     // タイムアウト(秒)
  const interval = options?.interval || 5;    // 状態確認間隔(秒) 最大値

  // タイムアウト時刻(ミリ秒)
  const timeLimit = Date.now() + (timeout * 1000);

  // 非同期処理実行要求
  const response = await _postRequestCommon({},
      reqPath, reqBody, [], {useLoadingImage: false});
  switch (response.status) {
    case HttpStatusCode.Accepted: break;  // 通常は 202 が返る
    case HttpStatusCode.Ok:
    default: return response;
  }

  // 実行状態取得URL - Location ヘッダー
  let statusCheck = response.headers?.location;
  if (!statusCheck) {
    console.error('Async API: No location header', response);
    return errorResponse('BFZ000001S');  // システム異常
  }
  if (statusCheck.startsWith('/')) {
    statusCheck = new URL(appConfig.API_URL).origin + statusCheck;
  }

  // 繰り返し実行状態を取得
  let asyncStatus;
  let waitSeconds = 1;
  const statusConfig = {
    validateStatus: function(status) {
      return validHttpStatus(status);
    },
    custom: {useLoadingImage: false},
  };
  while (true) {
    // 状態取得前に少し待つ (初回は1秒)
    const millis = waitSeconds * 1000;
    console.debug(`Async API: Waiting ${millis}ms`);
    await new Promise((resolve) => setTimeout(resolve, millis));

    // 実行状態取得 - まだ実行中なら 202 が返る
    asyncStatus = await axios.get(statusCheck, statusConfig);
    if (asyncStatus.status !== HttpStatusCode.Accepted) {
      break;
    }

    // タイムアウト判定
    const now = Date.now();
    if (timeLimit <= now) {
      console.error(`Async API: Timeout ${timeout}s`, asyncStatus);

      // タイムアウトメッセージ (デフォルトはシステム異常)
      const message = options?.onTimeout() || 'BFZ000001S';
      return errorResponse(message);
    }

    // 次は倍の時間待つ
    waitSeconds = Math.min(waitSeconds * 2, interval);
  }

  // レスポンスを返す
  return getResponse(asyncStatus);
}

/**
 * エラーレスポンス作成
 *
 * @param {string|object} message エラーメッセージ
 * @return {object} エラーメッセージを設定した疑似レスポンス
 */
function errorResponse(message) {
  return {
    data: {message: message},
  };
}

/**
 * バックエンドAPIのラッパーオブジェクト
 */
const backend = {

  // GETリクエスト
  get: async (path, params, options) => {
    const res = await _getRequestCommon(null,
        path?.startsWith('/') ? path.substring(1) : path,
        params,
        [],
        options);
    return new BackendResponse(res);
  },

  // POSTリクエスト
  post: async (path, body, options) => {
    const res = await _postRequestCommon(null,
        path?.startsWith('/') ? path.substring(1) : path,
        body,
        [],
        options);
    return new BackendResponse(res);
  },
};
export default backend;
